import { Temporal } from '@imago/api-client'
import { type Stripe_InvoiceID, type Stripe_PaymentIntentID } from '@imago/api-client-stripe'
import { type IS_Customer, type IS_Invoice, type IS_PaymentIntent, type IS_Subscription } from './stripe.ts'
import { type CareInfo } from './documents/Care.ts'
import { type InvoiceInfo } from './documents/Invoice.ts'
import { type ProductID } from './documents/ServiceProduct.ts'
import { type EMRInfo } from './documents/_EMR.ts'
import { GAHTTermination_ID } from './documents/GAHTTermination.ts'

export function buildInventoryInfo({myPayments}: {
	myPayments: Map<Stripe_PaymentIntentID, IS_PaymentIntent>
}): ProductID[] {
	return [...myPayments.values().map(p => p.metadata?.buy).filter(pid => pid !== undefined)]
}

export function buildUnpaidInvoicesInfo({myOpenInvoices}: {
	myOpenInvoices: Map<Stripe_InvoiceID, IS_Invoice>
}): InvoiceInfo[] {
	return [...myOpenInvoices.values().map((invoice): InvoiceInfo => {
		return {
			ID: invoice.id,
			number: invoice.number!,
			dueBy: invoice.due_date ? Temporal.Instant.fromEpochMilliseconds(invoice.due_date * 1000) : Temporal.Instant.fromEpochMilliseconds(invoice.effective_at! * 1000),
			periodStart: Temporal.Instant.fromEpochMilliseconds(invoice.lines.data[0]!.period.start * 1000),
			periodEnd: Temporal.Instant.fromEpochMilliseconds(invoice.lines.data[0]!.period.end * 1000),
			amountRemaining: invoice.amount_remaining / 100,
			nextAttemptOn: invoice.next_payment_attempt ? Temporal.Instant.fromEpochMilliseconds(invoice.next_payment_attempt * 1000) : null,
		}
	})]
}

export function buildCareInfo({myRecords, myCustomer, myAliveCareSubscription, myOpenInvoices}: {
	myRecords: EMRInfo[]
	myCustomer: IS_Customer | null
	myAliveCareSubscription: IS_Subscription | null
	myOpenInvoices: Map<Stripe_InvoiceID, IS_Invoice>
}): CareInfo {
	const wantsToCharge = myAliveCareSubscription && !myAliveCareSubscription.cancel_at_period_end
	const willCharge = wantsToCharge && (myAliveCareSubscription.collection_method === 'charge_automatically' ? (myAliveCareSubscription.default_payment_method ?? (myCustomer && myCustomer.invoice_settings?.default_payment_method)) : true)
	const nextCharge = willCharge ? Temporal.Instant.fromEpochMilliseconds(myAliveCareSubscription.items.data[0]!.current_period_end * 1000) : null

	const lastTerminationTS = myRecords.flatMap(record => record.type === GAHTTermination_ID ? [record.timestamp] : []).toSorted((a, b) => -Temporal.Instant.compare(a, b))[0] ?? null

	const unpaidInvoices = buildUnpaidInvoicesInfo({myOpenInvoices})

	return nextCharge ? {
		terminated: null,
		nextCharge,
		unpaidInvoices,
	} : {
		terminated: lastTerminationTS,
		nextCharge,
		unpaidInvoices,
	}
}
